/**
 * Settings → Departments: which tiles the dashboard shows (fpa-backend ADR-0006). "Configured tiles
 * only" (the default, as FP&A's original dashboard) keeps automatic tiles — Sage departments and
 * cost centers no tile groups — off the sidebar, overview, ranking and search; their money is never
 * dropped: it is listed here with the selected period's amounts, and the overview says how much sits
 * outside. "All departments" shows every tile. Changing it affects everyone and is audit-logged.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { errorMessage } from '@/api/client'
import type { TileMode } from '@/api/types'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Panel } from '@/components/ui/Panel'
import { useRunwayModel } from '@/features/runway/RunwayContext'
import { formatMoney } from '@/lib/format'

import styles from './TileModePanel.module.css'
import { useSetTileMode } from './useSettings'

const MODE_LABEL: Record<TileMode, string> = {
  configured: 'Configured tiles only',
  all: 'All departments',
}

const MODE_HELP: Record<TileMode, string> = {
  configured:
    'The dashboard shows the configured tiles. Sage departments no tile groups stay off the sidebar, overview, ranking and search; their spend is listed below and noted on the overview.',
  all: 'The dashboard shows every department, including the automatic tiles for Sage departments no tile groups.',
}

const GROUPING: Record<string, string> = {
  sage_hierarchy: 'Sage parent + sub-departments',
  sage: 'Sage department',
  custom: 'Custom tile',
}

function ModeOption({
  mode,
  current,
  disabled,
  onPick,
}: {
  mode: TileMode
  current: TileMode
  disabled: boolean
  onPick: (mode: TileMode) => void
}) {
  const handleClick = useCallback(() => onPick(mode), [onPick, mode])
  return (
    <button
      type="button"
      className={styles.option}
      aria-pressed={current === mode}
      disabled={disabled}
      onClick={handleClick}
    >
      {MODE_LABEL[mode]}
    </button>
  )
}

export function TileModePanel() {
  const { runway } = useRunwayModel()
  const mode = runway.tile_mode
  const outside = runway.outside_tiles
  const save = useSetTileMode()
  const [pending, setPending] = useState<TileMode | null>(null)
  const { hash } = useLocation()
  const outsideRef = useRef<HTMLElement>(null)

  // The overview's "outside the dashboard" link lands on the list.
  useEffect(() => {
    const el = outsideRef.current
    if (hash === '#outside' && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' })
  }, [hash])

  const pick = useCallback((next: TileMode) => setPending(next === mode ? null : next), [mode])
  const { mutate, reset } = save
  const cancel = useCallback(() => {
    reset()
    setPending(null)
  }, [reset])
  const confirm = useCallback(() => {
    if (!pending) return
    mutate(pending, { onSuccess: () => setPending(null) })
  }, [mutate, pending])

  return (
    <Panel title="Dashboard tiles" aside={`Amounts: ${runway.period.label}`}>
      <div className={styles.modes} role="group" aria-label="Tiles on the dashboard">
        <ModeOption mode="configured" current={mode} disabled={save.isPending} onPick={pick} />
        <ModeOption mode="all" current={mode} disabled={save.isPending} onPick={pick} />
      </div>
      <p className={styles.help}>{MODE_HELP[mode]}</p>

      <section id="outside" ref={outsideRef} aria-label="Departments outside the dashboard">
        {mode === 'configured' && outside.count > 0 ? (
          <>
            <h4 className={styles.subhead}>
              {formatMoney(outside.actual)} in {outside.count} {outside.count === 1 ? 'department' : 'departments'}{' '}
              outside the dashboard
            </h4>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Department</th>
                    <th scope="col">Grouping</th>
                    <th scope="col" className={styles.num}>
                      Actual
                    </th>
                    <th scope="col" className={styles.num}>
                      Budget
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {outside.tiles.map((t) => (
                    <tr key={t.code}>
                      <td>
                        <b>{t.name}</b> <span className={styles.code}>{t.code}</span>
                      </td>
                      <td className={styles.muted}>
                        {GROUPING[t.grouping] ?? t.grouping}
                        {t.source_depts.length > 1 ? ` (${t.source_depts.length})` : ''}
                      </td>
                      <td className={styles.num}>{formatMoney(t.actual)}</td>
                      <td className={styles.num}>{t.budget > 0 ? formatMoney(t.budget) : 'no budget'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.help}>
              To show one on the dashboard, add its Sage department to a custom tile below, or switch to &ldquo;All
              departments&rdquo;.
            </p>
          </>
        ) : mode === 'configured' ? (
          <p className={styles.help}>Every department with spend or budget in {runway.period.label} has a tile.</p>
        ) : null}
      </section>

      {pending ? (
        <ConfirmDialog
          title={`Show ${MODE_LABEL[pending].toLowerCase()}?`}
          confirmLabel="Change for everyone"
          busy={save.isPending}
          error={save.isError ? errorMessage(save.error, 'Could not change the dashboard tiles.') : null}
          onConfirm={confirm}
          onCancel={cancel}
        >
          <p>{MODE_HELP[pending]}</p>
          <p>The dashboard changes for everyone. Company totals follow the tiles shown; no money is lost either way.</p>
        </ConfirmDialog>
      ) : null}
    </Panel>
  )
}
